import { useState } from 'react';
import { RefreshCw, Trash2, CheckCircle, AlertCircle, Server } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';

const AdminSettingsPage = () => {
  const [clearing, setClearing] = useState(false);

  const clearAllCache = async () => {
    setClearing(true);
    
    try {
      // Clear localStorage
      localStorage.clear();
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Clear browser cache using Cache API (if available)
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }
      
      // Unregister service workers (if any)
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(registration => registration.unregister())
        );
      }
      
      toast.success('Cache vidé avec succès ! La page va se recharger...');
      
      // Force reload after a short delay
      setTimeout(() => {
        window.location.reload(true);
      }, 1500);
      
    } catch (error) {
      console.error('Error clearing cache:', error);
      toast.error('Erreur lors du vidage du cache');
    } finally {
      setClearing(false);
    }
  };

  const forceRefresh = () => {
    // Hard reload - bypass cache
    window.location.href = window.location.href.split('?')[0] + '?v=' + Date.now();
  };

  return (
    <AdminLayout title="Paramètres">
      <div className="space-y-6 max-w-2xl" data-testid="admin-settings">
        
        {/* Version Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="w-5 h-5 text-emerald-600" />
              Informations de version
            </CardTitle>
            <CardDescription>
              Version actuelle de l'application
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-stone-50 rounded-xl p-4 font-mono text-sm">
              <div className="flex justify-between items-center mb-2">
                <span className="text-stone-500">Version :</span>
                <span className="text-stone-900 font-semibold">v1.3.0</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-stone-500">Build :</span>
                <span className="text-stone-900">31/03/2026 à 17:49</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-stone-500">Environnement :</span>
                <span className="text-emerald-600 font-medium">Production</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cache Management */}
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-amber-600" />
              Gestion du cache
            </CardTitle>
            <CardDescription>
              Videz le cache du navigateur pour forcer le rechargement des dernières modifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-800 font-medium text-sm mb-1">Quand vider le cache ?</p>
                  <ul className="text-amber-700 text-sm space-y-1">
                    <li>• Après un nouveau déploiement du site</li>
                    <li>• Si vous voyez des données obsolètes</li>
                    <li>• En cas de problème d'affichage</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={clearAllCache}
                disabled={clearing}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="clear-cache-btn"
              >
                {clearing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Vidage en cours...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Vider tout le cache
                  </>
                )}
              </Button>
              
              <Button
                onClick={forceRefresh}
                variant="outline"
                className="flex-1"
                data-testid="force-refresh-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Forcer le rafraîchissement
              </Button>
            </div>

            <p className="text-stone-500 text-xs">
              Note : Le vidage du cache vous déconnectera et rechargera la page.
            </p>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Instructions pour les utilisateurs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-stone-600 text-sm mb-4">
              Si vos clients ne voient pas les dernières modifications du site, conseillez-leur de :
            </p>
            <div className="bg-stone-50 rounded-xl p-4 space-y-3">
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 font-semibold text-sm flex-shrink-0">1</span>
                <div>
                  <p className="text-stone-800 font-medium">Windows / Linux</p>
                  <p className="text-stone-500 text-sm">Appuyer sur <kbd className="bg-stone-200 px-1.5 py-0.5 rounded text-xs">Ctrl</kbd> + <kbd className="bg-stone-200 px-1.5 py-0.5 rounded text-xs">F5</kbd></p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 font-semibold text-sm flex-shrink-0">2</span>
                <div>
                  <p className="text-stone-800 font-medium">Mac</p>
                  <p className="text-stone-500 text-sm">Appuyer sur <kbd className="bg-stone-200 px-1.5 py-0.5 rounded text-xs">Cmd</kbd> + <kbd className="bg-stone-200 px-1.5 py-0.5 rounded text-xs">Shift</kbd> + <kbd className="bg-stone-200 px-1.5 py-0.5 rounded text-xs">R</kbd></p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 font-semibold text-sm flex-shrink-0">3</span>
                <div>
                  <p className="text-stone-800 font-medium">Mobile</p>
                  <p className="text-stone-500 text-sm">Effacer les données de navigation dans les paramètres du navigateur</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>
    </AdminLayout>
  );
};

export default AdminSettingsPage;
